/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ListBulletsIcon, MicrophoneIcon, PaperPlaneRightIcon, StopIcon
} from '@phosphor-icons/react';
import { Flex } from 'antd';
import React, {
  forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState
} from 'react';
// import { Container } from 'react-bootstrap';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { Typography } from 'tfc-components';

import Modal from '../Modal';

import UploadMultipleFiles from './uploadFiles';

import Icon from 'components/atoms/Icon';
import { ImageMessageItemTypes } from 'components/molecules/ImageMessage';
import MessageFile, { MessageFileProps } from 'components/molecules/MessageFile';
import { useToast } from 'components/molecules/Toast';
import Camera from 'components/organisms/Camera';
import DynamicTextarea, { DynamicTextareaRef } from 'components/organisms/DynamicTextarea';
import SpeechToText from 'components/organisms/SpeechToText';
import { ContextChatbot } from 'components/templates/MainlayoutChatbot';
import useClickOutside from 'hooks/useClickOutside';
import usePermission from 'hooks/usePermission';
import { deleteFileConversationChatBotService, getAiAssistantService } from 'services/accountConversations';
import { getLocalStorage } from 'services/common/storage';
import { LOCAL_STORAGE_CHATBOT } from 'utils/constants';
import mapModifiers, {
  detectFileType, formatBytes, getFileExtension, getTextFromHTML
} from 'utils/functions';

export type QuestionConfirmType = 'canChat' | 'enterPhone' | 'verifyByOTP' | 'enterMail' | 'anonymous' | '';
export type RoleUserType = 'guest' | 'staff' | '';

export const DEFAULT_FORMCHAT = {
  question: '',
  images: undefined,
  files: undefined,
};

export type ChatbotFormReturnTypes = {
  question: string;
  streamType?: string;
  files?: UploadFileType[];
  action?: string;
};

interface ChatBotFooterProps {
  method: UseFormReturn<ChatbotFormReturnTypes>;
  color?: string;
  questionConfirmType?: QuestionConfirmType;
  isProcessing?: boolean;
  onSubmit: (params: ChatbotFormReturnTypes) => void;
  onSubmitPhone: (params: ChatbotFormReturnTypes) => void;
  onSubmitMail: (params: ChatbotFormReturnTypes) => void;
  onSubmitVerifyOTP: (params: ChatbotFormReturnTypes) => void;
  conversationCode: string;
  disabledUploadFiles?: boolean;
  disabledVoiceRecord?: boolean;
  onCloseStartVoice: () => void;
  onStopStreaming?: () => void;
  isOtpExceeded?: boolean;
  isDisplay?: boolean;
  showSuggestionMsg?: boolean;
  suggestedMsg?: string[];
  conversationLength?: number;
  isVerified?: boolean;
  isCustomize?: boolean;
  mainColor?: string;
  typeVerify?: number;
}

export type TextAreaInputChatBotTypes = {
  resetStyleInput: () => void;
  hideSuggestionMsg: () => void;
  showSuggestionMsg: () => void;
};

const ChatBotFooter = forwardRef<TextAreaInputChatBotTypes, ChatBotFooterProps>(
  (
    {
      method,
      questionConfirmType,
      color,
      onSubmit,
      onSubmitPhone,
      onSubmitMail,
      onSubmitVerifyOTP,
      onCloseStartVoice,
      isProcessing,
      conversationCode,
      disabledUploadFiles,
      disabledVoiceRecord,
      onStopStreaming,
      isOtpExceeded,
      isDisplay,
      suggestedMsg,
      conversationLength,
      isVerified,
      isCustomize,
      mainColor,
      typeVerify
    },
    ref
  ) => {
    const phoneRegex = /^0\d{0,9}$/;
    const { showToast } = useToast();
    const [permission, setPermission] = usePermission({ type: 'microphone' });
    const { t } = useTranslation();

    const { isDirty, isValid } = method.formState;
    const [isShowCamera, setIsShowCamera] = useState<boolean>(false);
    const [fileList, setFileList] = useState<UploadFileType[]>([]);
    const [isChunking, setIsChunking] = useState(false);
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [microphonePermissionAlertShow, setMicrophonePermissionAlertShow] = useState(false);
    const [requestOpionsShow, setRequestOpionsShow] = useState(false);
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
    const [activeOption, setActiveOption] = useState(-1);
    const [showUploadedFiles, setShowUploadedFiles] = useState(false);
    const [microphonePermissionShow, setMicrophonePermissionShow] = useState(permission === 'prompt');
    const [isShowSuggestionMsg, setIsShowSuggestionMsg] = useState(true);

    const [searchParams] = useSearchParams();
    const modeView = searchParams.get('mode') === 'view';
    const botId = (window as any).currentBotPath;
    const role = getLocalStorage(LOCAL_STORAGE_CHATBOT.ROLE + botId);

    const infoChatbot = useContext(ContextChatbot);

    // Refs
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const dynamicTextareaRef = useRef<DynamicTextareaRef>(null);
    const attachedFileRef = useRef<HTMLDivElement>(null);

    useClickOutside(attachedFileRef, () => {
      if (!isChunking) setShowUploadedFiles(false);
    });

    // const canUseMic = !method.watch('question') && fileList.length === 0;
    // const filesWatch = method.watch('files');

    // Queries
    const { data: requestOptionsData } = useQuery(
      ['requestOptions', conversationCode],
      () => getAiAssistantService(conversationCode),
      {
        enabled: !!conversationCode
          && !!getLocalStorage(LOCAL_STORAGE_CHATBOT.ACCESSTOKEN_CHATBOT + botId),
      }
    );

    const { mutate: removeFileMutate } = useMutation(
      ['deleteFile', conversationCode],
      (files?: UploadFileType[]) => deleteFileConversationChatBotService(conversationCode, {
        codes: files?.map((f) => f.fileSubmit?.code || '') || [],
      }),
      {
        onSuccess: () => {
          // setFileList(fileList.filter((f) => f?.fileSubmit?.code !== id));
        },
      }
    );

    // Functions
    const handleRemoveFile = (e: any, id?: string) => {
      e.preventDefault();
      e.stopPropagation();
      const file = fileList.find((f) => f?.id === id);
      if (!file) return;
      removeFileMutate([file]);
      const idxExisted = fileList.findIndex((f) => f.id === id);
      const newFileList = [...fileList];
      if (idxExisted !== -1) newFileList.splice(idxExisted, 1);
      setFileList(newFileList);
    };

    const handleChunkLoading = (isLoading: boolean) => {
      setIsChunking(isLoading);
    };

    const imgFilesState: ImageMessageItemTypes[] = useMemo(
      () => fileList
        .filter((f) => f?.file?.type?.startsWith('image/'))
        ?.map((i) => ({
          id: i?.file?.lastModified.toString() || '',
          src: i?.file ? URL.createObjectURL(i?.file) : '',
          alt: i?.file?.name || '',
          status: i?.status as 'loading' | 'error' | 'success',
          handleClick: () => handleRemoveFile(i?.id),
        })),
      [fileList]
    );

    const filesState: (MessageFileProps & { id: string })[] = useMemo(
      () => fileList
        // .filter((f) => !f?.file?.type?.startsWith('image/'))
        ?.map((i) => ({
          id: i?.id ?? '',
          type: i?.file?.type?.startsWith('image/') ? 'image' : 'file',
          fileIcon: 'fileOutline',
          fileName: i?.file?.name || '',
          fileType: i?.file?.type
            ? getFileExtension(i?.file?.name) : detectFileType(i?.file?.type),
          fileSize: i?.file?.size ? formatBytes(i?.file?.size) : '',
          imgSrc: i?.file ? URL.createObjectURL(i?.file) : '',
          imgAlt: i?.file?.name || '',
          status: i?.status as 'loading' | 'error' | 'success',
          handleClick: () => handleRemoveFile(i?.id),
        })),
      [fileList]
    );

    const successImagesLength = useMemo(
      () => imgFilesState.filter((f) => f.status === 'success').length,
      [imgFilesState]
    );
    const successFilesLength: number = useMemo(
      () => filesState.filter((f) => f.status === 'success').length - successImagesLength,
      [filesState, successImagesLength]
    );

    const isFileLoading = useMemo(() => filesState.some((file) => file.status === 'loading'), [filesState]);

    const hasValues = !!method.watch('question');

    const detectTextInput = (event?: any, reset?: boolean) => {
      if (textAreaRef.current === null) return;
      if (reset) {
        textAreaRef.current.style.height = 'auto';
        return;
      }
      if (textAreaRef.current.scrollHeight > 94) {
        textAreaRef.current.style.overflowY = 'scroll';
        textAreaRef.current.style.overflowX = 'hidden';
      } else {
        textAreaRef.current.style.height = 'auto';
        textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      }
    };

    const handleChooseFile = (fileData: UploadFileType) => {
      setShowUploadedFiles(true);
      setFileList((prevFilesState) => {
        const exists = prevFilesState.some((item) => item.id === fileData.id);
        if (exists) {
          return prevFilesState.map((item) => (item.id === fileData.id ? fileData : item));
        }
        return [...prevFilesState, fileData];
      });

      setFileList((prevFileList) => {
        const exists = prevFileList.some((item) => item.id === fileData.id);
        if (exists) {
          return prevFileList.map((item) => (item.id === fileData.id ? fileData : item));
        }
        return [...prevFileList, fileData];
      });
    };

    const handleSubmit = method.handleSubmit(() => {
      if (questionConfirmType === 'enterPhone') {
        onSubmitPhone({ question: method.getValues('question') });
        dynamicTextareaRef.current?.resetPhone();
      } else if (questionConfirmType === 'enterMail') {
        onSubmitMail({ question: method.getValues('question') });
      } else if (questionConfirmType === 'verifyByOTP') {
        onSubmitVerifyOTP({ question: method.getValues('question') });
      } else if (questionConfirmType !== '') {
        const submitData = {
          ...method.getValues(),
          action: requestOptionsData?.[activeOption]?.serviceData.action,
          question: getTextFromHTML(method.getValues('question')),
          files:
            fileList.length > 0
              ? fileList.filter((file) => file !== undefined && file.status === 'success')
              : undefined,
        };
        setIsShowSuggestionMsg(false);
        onSubmit(submitData);
        setSelectedOptionIndex(-1);
        setActiveOption(-1);
      }
      // detectTextInput();
      setFileList([]);
    });

    const handleClick = () => {
      if (permission === 'granted') {
        setIsRecording(true);
      } else if (permission === 'denied') {
        setMicrophonePermissionAlertShow(true);
      } else {
        setMicrophonePermissionShow(true);
      }
    };

    const handleAllow = async () => {
      setMicrophonePermissionShow(false);
      try {
        await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        setPermission('granted');
        setIsRecording(true);
      } catch (error) {
        setPermission('denied');
        setMicrophonePermissionAlertShow(true);
      }
    };

    // const handleTextAreaOptionClick = (option: ToolboxOption) => {
    //   dynamicTextareaRef.current?.insertOption(option);
    //   dynamicTextareaRef.current?.focus();
    //   setRequestOpionsShow(false);
    // };

    useEffect(() => {
      if (textAreaRef.current) {
        textAreaRef.current.style.overflowY = 'hidden';
        textAreaRef.current.style.overflowX = 'hidden';
        textAreaRef.current.style.height = 'auto';

        textAreaRef.current.addEventListener('input', detectTextInput);
        textAreaRef.current?.addEventListener('focusout', detectTextInput);
        textAreaRef.current?.addEventListener('mousedown', detectTextInput);
      }

      return () => {
        textAreaRef.current?.removeEventListener('input', detectTextInput);
        textAreaRef.current?.removeEventListener('focusout', detectTextInput);
        textAreaRef.current?.removeEventListener('mousedown', detectTextInput);
      };
    }, []);

    useImperativeHandle(ref, () => ({
      resetStyleInput: () => {
        detectTextInput(null, true);
        textAreaRef.current?.blur();
      },
      hideSuggestionMsg: () => {
        setIsShowSuggestionMsg(false);
      },
      showSuggestionMsg: () => {
        setIsShowSuggestionMsg(true);
      }
    }));

    useEffect(() => {
      setIsShowSuggestionMsg((isVerified && conversationCode !== '') ?? false);
    }, [conversationCode, isVerified]);

    useEffect(() => {
      if (permission === 'denied' && microphonePermissionAlertShow) {
        showToast({
          message: t('chatbot.microphoneRequest'),
          show: true,
          type: 'error',
        });
      }
    }, [permission, microphonePermissionAlertShow]);

    const handleClickSuggestMsg = (word: string) => {
      if (isProcessing) return;
      setIsShowSuggestionMsg(false);
      method.setValue('question', word, { shouldValidate: true });
      method.handleSubmit((values) => {
        const submitData = {
          ...values,
          question: getTextFromHTML(word),
          action: requestOptionsData?.[activeOption]?.serviceData.action,
        };
        onSubmit(submitData);
      })();
    };

    return (
      <>
        <div
          style={{ '--chatbot-color': color, '--message-bg-color': mainColor } as React.CSSProperties}
          className={mapModifiers('o-chatBotFooter', isRecording && 'recording', isCustomize && 'customize')}
        >
          <div className="o-chatBotFooter_suggestList o-chatBotFooter_suggestListMsg">
            <div className="o-chatBotFooter_suggestListMsg_inner">
              {isCustomize && suggestedMsg?.map((word, index) => (
                <div
                  key={`${word}-${index.toString()}`}
                  onClick={() => handleClickSuggestMsg(word)}
                  className="o-chatBotFooter_suggestedItemMsg"
                >
                  <Typography.Text extendClasses="fs-14x20">{word}</Typography.Text>
                </div>
              ))}
              {/* {!isProcessing && (typeVerify === 3 ? conversationLength === 1
                : conversationCode !== '' && isShowSuggestionMsg)
                && suggestedMsg && suggestedMsg.length > 0
                && suggestedMsg.map((word, index) => (
                  <div
                    key={`${word}-${index.toString()}`}
                    onClick={() => handleClickSuggestMsg(word)}
                    className="o-chatBotFooter_suggestedItemMsg"
                  >
                    <Typography.Text fontweight="600">{word}</Typography.Text>
                  </div>
                ))} */}
              {isShowSuggestionMsg
                && suggestedMsg && suggestedMsg.length > 0
                && suggestedMsg.map((word, index) => (
                  <div
                    key={`${word}-${index.toString()}`}
                    onClick={() => handleClickSuggestMsg(word)}
                    className="o-chatBotFooter_suggestedItemMsg"
                  >
                    <Typography.Text fontweight="600">{word}</Typography.Text>
                  </div>
                ))}
            </div>
          </div>
          <div className="o-chatBotFooter_wrapper" ref={attachedFileRef}>
            <div className="o-chatBotFooter_area">
              {/* List file */}
              {
                filesState.length > 0
                && (
                  <div className="o-chatBotFooter_listFile">
                    {filesState.length > 0
                      && filesState.map((item, idx) => (
                        <div className="o-chatBotFooter_area_uploadContent_item" key={`file-item-${idx.toString()}`}>
                          <MessageFile
                            {...item}
                            fileIcon={item.fileIcon}
                            fileSize={item.fileSize}
                            onRemove={(isFileLoading
                              || disabledUploadFiles
                              || isChunking
                              || modeView)
                              ? undefined : handleRemoveFile}
                            color={color}
                          />
                        </div>
                      ))}
                  </div>
                )
              }

              {isRecording ? (
                <SpeechToText
                  conversationCode={conversationCode}
                  onCloseStartVoice={onCloseStartVoice}
                  onRecordingText={(textRecording) => {
                    method.setValue('question', `${textAreaRef.current?.value || ''} ${textRecording?.trim() || ''}`, { shouldDirty: true, shouldValidate: true });
                  }}
                  isRecording={isRecording}
                  disabled={isProcessing || isChunking}
                  onClose={() => setIsRecording(false)}
                  onMicrophonePermissionsDenied={() => setMicrophonePermissionAlertShow(true)}
                  mainColor={color}
                />
              ) : (
                <div className="o-chatBotFooter_area_input">
                  <Controller
                    name="question"
                    control={method.control}
                    render={({ field, fieldState }) => (!conversationCode ? (
                      <textarea
                        className="o-chatBotFooter_area_input_textarea"
                        {...field}
                        ref={textAreaRef}
                        disabled={isProcessing || questionConfirmType === '' || modeView}
                        rows={1}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (questionConfirmType === 'enterPhone') {
                            if (phoneRegex.test(input) || input === '') {
                              field.onChange(e);
                            }
                          } else {
                            field.onChange(e);
                          }
                        }}
                        onKeyDown={(e) => {
                          const input = e.currentTarget.value;
                          if (e.which === 13) {
                            e.preventDefault();

                            if (questionConfirmType === 'enterPhone') {
                              if (phoneRegex.test(input) || input === '') {
                                onSubmitPhone({ question: e.currentTarget.value });
                              }
                            } else if (questionConfirmType === 'verifyByOTP') {
                              onSubmitVerifyOTP({ question: e.currentTarget.value });
                            } else if (questionConfirmType !== '') {
                              handleSubmit();
                            }
                          }
                        }}
                        placeholder={t('chatbot.placeholderInput')}
                      // placeholder={role
                      // === 'staff' ? 'Gõ "/" để chọn tin nhắn nhanh' : 'Hỏi bất kỳ điều gì'}
                      />
                    ) : (
                      <>
                        <DynamicTextarea
                          {...field}
                          ref={dynamicTextareaRef}
                          confirmType={questionConfirmType}
                          disableEnter={isFileLoading || isProcessing || !isDirty || !isValid}
                          disabled={
                            isDisplay
                            || questionConfirmType === ''
                            || modeView
                            || isOtpExceeded

                          }
                          placeholder={field.value?.replace('<br>', '') ? '' : t('chatbot.placeholderInput')}
                          className="custom-textarea"
                          options={requestOptionsData || []}
                          showOptions={requestOpionsShow}
                          onShowOptions={(value: boolean) => setRequestOpionsShow(value)}
                          onChange={(text) => {
                            // field.onChange(t);
                            method.setValue('question', text, { shouldDirty: true, shouldValidate: true });
                          }}
                          selectedOptionIndex={selectedOptionIndex}
                          onChangeSelectedOptionIndex={
                            (setIndex: number) => setSelectedOptionIndex(setIndex)
                          }
                          activeOptionIndex={activeOption}
                          onChangeActiveOptionIndex={
                            (setIndex: number) => setActiveOption(setIndex)
                          }
                          onSubmit={(e) => {
                            const input = e.currentTarget.innerText;
                            e.preventDefault();

                            if (questionConfirmType === 'enterPhone') {
                              if (phoneRegex.test(input) || input === '') {
                                onSubmitPhone({ question: e.currentTarget.innerText });
                              }
                            } else if (questionConfirmType === 'verifyByOTP') {
                              onSubmitVerifyOTP({ question: e.currentTarget.innerText });
                            } else if (questionConfirmType !== '') {
                              handleSubmit();
                            }
                            dynamicTextareaRef.current?.focus();
                          }}
                          onFocus={() => {
                            if (!isChunking) setShowUploadedFiles(false);
                          }}
                        />
                        {fieldState.error && <div className="a-inputCustom-errText">{fieldState.error.message}</div>}

                      </>

                    ))}
                  />
                  <div className="o-chatBotFooter_actions">
                    <div className="o-chatBotFooter_actions_left">
                      {(role === 'staff' || isCustomize) && (
                        <button
                          type="button"
                          className="o-chatBotFooter_actions_button"
                          disabled={
                            isDisplay
                            || disabledUploadFiles
                            || isProcessing
                            || isChunking
                            || questionConfirmType === ''
                            || modeView
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setRequestOpionsShow(!requestOpionsShow);
                          }}
                        >
                          <ListBulletsIcon size={20} color="#1F1F1F" />
                        </button>
                      )}
                      <Controller
                        name="files"
                        control={method.control}
                        render={({ field }) => (
                          <UploadMultipleFiles
                            conversationCode={conversationCode}
                            handleChooseFile={(fileData) => {
                              field.onChange(fileData);
                              handleChooseFile(fileData);
                            }}
                            handleChunkLoading={handleChunkLoading}
                            disabled={
                              isDisplay
                              || disabledUploadFiles
                              || isProcessing
                              || isChunking
                              || modeView
                            }
                            filesState={filesState}
                            totalFileLength={filesState.length}
                            fileLengthValues={successFilesLength}
                            imgLengthValues={successImagesLength}
                            fileLimit={3}
                            imgLimit={9}
                            type="uploadFiles"
                            onClick={() => {
                              setShowUploadedFiles(!showUploadedFiles);
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="files"
                        control={method.control}
                        render={({ field }) => (
                          <UploadMultipleFiles
                            conversationCode={conversationCode}
                            handleChooseFile={(fileData) => {
                              field.onChange(fileData);
                              handleChooseFile(fileData);
                            }}
                            disabled={
                              isDisplay
                              || disabledUploadFiles
                              || isProcessing
                              || isChunking
                              || modeView
                            }
                            filesState={filesState}
                            fileLengthValues={successFilesLength}
                            imgLengthValues={successImagesLength}
                            fileLimit={3}
                            imgLimit={9}
                            type="camera"
                            handleChunkLoading={handleChunkLoading}
                            onCamera={() => setIsShowCamera(true)}
                          />
                        )}
                      />
                      {
                        (infoChatbot.tierData?.allowS2t || isCustomize)
                        && (
                          <button
                            type="button"
                            className="o-chatBotFooter_actions_button"
                            disabled={
                              isDisplay
                              || disabledVoiceRecord
                              || isProcessing
                              || isChunking
                              || questionConfirmType === ''
                              || modeView

                            }
                            onClick={() => {
                              if (permission === 'denied') {
                                showToast({
                                  message: t('chatbot.cameraPermissionDenied'),
                                  show: true,
                                  type: 'error',
                                });
                              } else {
                                handleClick?.();
                              }
                            }}
                          >
                            <MicrophoneIcon size={20} color="#1F1F1F" />
                          </button>
                        )
                      }
                    </div>
                    {isProcessing ? (
                      <div className="o-chatBotFooter_area_button_tooltipWrapper">
                        <button
                          type="button"
                          className="o-chatBotFooter_area_button"
                          onClick={() => {
                            onStopStreaming?.();
                          }}
                        >
                          <Icon
                            iconName="stop"
                            size="20x20"
                          />
                        </button>
                        <span className="o-chatBotFooter_area_button_tooltip">{t('chatbot.stop')}</span>
                      </div>
                    ) : (
                      (!isDisplay || isCustomize) && (
                        <button
                          type="button"
                          className="o-chatBotFooter_area_button"
                          disabled={
                            isFileLoading
                            || !hasValues
                            || method.getValues('question') === '<br>'
                            || isProcessing
                            || isChunking
                            || questionConfirmType === ''
                            || modeView
                            || isOtpExceeded
                            || !isValid
                            || !isDirty
                          }
                          onClick={() => {
                            if (questionConfirmType !== '' && hasValues) {
                              handleSubmit();
                            }
                          }}
                        >
                          <Icon
                            iconName={
                              isFileLoading
                                || !hasValues
                                || method.getValues('question') === '<br>'
                                || isProcessing
                                || isChunking
                                || questionConfirmType === ''
                                || modeView
                                || isOtpExceeded
                                ? 'paperPlane'
                                : 'activePaperPlane'
                            }
                            size="20x20"
                          />
                        </button>
                      )
                    )}

                    {isDisplay && !isCustomize && (
                      <button type="button" className="o-chatBotFooter_area_button">
                        <Icon iconName="paperPlane" size="20x20" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Flex justify="center" className="o-chatBotFooter_powered">
            <Typography.Text extendClasses="fs-14x18 o-chatBotFooter_powered_text o-chatBotFooter_powered_dropdown">Powered by Ask2Auto</Typography.Text>
          </Flex>
          {/* <Modal
            isOpen={permissionMicro}
            cancelButton={{
              handleClick: () => {
                setPermissionMicro(false);
              },
              text: 'Đã hiểu',
            }}
          >
            <Typography.Text textStyle="center" extendClasses="t-chatting_modalWarning_description">
              Vui lòng cấp quyền truy cập microphone.
            </Typography.Text>
          </Modal> */}

          <Modal
            isOpen={permission !== 'granted' && microphonePermissionShow}
            cancelButton={{
              handleClick: () => {
                setMicrophonePermissionShow(false);
              },
              text: t('chatbot.cancel'),
            }}
            okButton={{
              handleClick: handleAllow,
              text: t('chatbot.accept'),
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <MicrophoneIcon size={60} color="#1F1F1F" />
              <Typography.Text textStyle="center" extendClasses="t-chatting_modalWarning_description">
                {t('chatbot.permissionDenined')}
              </Typography.Text>
            </div>
          </Modal>
        </div>
        <Camera
          isShow={isShowCamera}
          conversationCode={conversationCode}
          handleChooseFile={(fileData) => {
            handleChooseFile(fileData);
            setIsShowCamera(false);
          }}
          closeCamera={() => setIsShowCamera(false)}
        />
      </>
    );
  }
);

export default ChatBotFooter;
